import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  IconButton,
  Grid,
  InputLabel,
  TextField,
  Typography,
  Box,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import DatePicker from "react-datepicker";
import CustomButton from "../../Custom/CustomButton";
import instance from "../../../globals/axios";
import { getFarmerPlotId } from "../../../globals/utils";
import { useAlert } from "../../Alert";
import EditIcon from "../../../assets/svg/EditIcon.svg";
import SuccessPopup from "./SuccessPopup";
import { getAuth } from "../../../globals/authentication";
import { SERVER_BASE_ADDRESS } from "../../../globals/const";
import axios from "axios";

import ImageCard from "../../ImagesDisplay";
const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    fontSize: 16,
  },
  dialogContent: {
    height: 500,
  },
  datepicker: {
    display: "none",
  },
  textLabel: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "400 !important",
    color: "#000",
    marginTop: "10px",
  },
  textTitle: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "700 !important",
    color: "#000",
  },
}));

const AbnormilityPopup = ({ onClose, t }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [abnormalityArea, setAbnormalityArea] = useState("");
  const [typeOfAbnormality, setTypeOfAbnormality] = useState("");
  const [damagePercentage, setDamagePercentage] = useState("");
  const { showAlert } = useAlert();
  const [existingData, setExistingData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [comment, setComment] = useState("");
  const [errors, setErrors] = useState({
    selectedDate: "",
    abnormalityArea: "",
    typeOfAbnormality: "",
    damagePercentage: "",
    image: "",
  });
  let farmerPlotId = getFarmerPlotId();
  const user = getAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await instance(
          `plotengagement/get-abnormalities?farmer_plot=${farmerPlotId}`
        );
        let data = response.data;
        if (data.data.length > 0) {
          setExistingData(data.data[0]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (farmerPlotId) {
      fetchData();
    }
  }, [farmerPlotId]);

  useEffect(() => {
    if (existingData) {
      const {
        date_of_activity,
        abnormality_area,
        type_of_abnormality,
        damage_percentage,
        images,
      } = existingData;

      setSelectedDate(date_of_activity ? new Date(date_of_activity) : null);
      setAbnormalityArea(abnormality_area || "");
      setTypeOfAbnormality(type_of_abnormality || "");
      setDamagePercentage(damage_percentage || "");

      if (images && images.length > 0) {
        setUploadedImages(images.map((img) => img.URL));
      }
    }
  }, [existingData]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setErrors((prevErrors) => ({
      ...prevErrors,
      selectedDate: "",
    }));
  };

  const handleAbnormalityAreaChange = (event) => {
    setAbnormalityArea(event.target.value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      abnormalityArea: "",
    }));
  };
  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    setUploadedImages((prevImages) => [...prevImages, ...files]);

    event.target.value = null;
  };
  const handleDeleteImage = (imageUrl) => {
    setUploadedImages((prevImages) =>
      prevImages.filter((url) => url !== imageUrl)
    );
  };

  const handleTypeOfAbnormalityChange = (event) => {
    setTypeOfAbnormality(event.target.value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      typeOfAbnormality: "",
    }));
  };

  const handleDamageChange = (event) => {
    let { value } = event.target;
    if (!isNaN(value) || value === "") {
      value = Math.min(Math.max(value, 0), 100);
      setDamagePercentage(value.toString());

      setErrors((prevErrors) => ({
        ...prevErrors,
        damagePercentage: "",
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        damagePercentage: t(
          "Invalid input. Please enter a number between 0 and 100."
        ),
      }));
    }
  };
  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };

  const validateForm = () => {
    let formValid = true;
    let updatedErrors = {};

    if (!selectedDate) {
      updatedErrors.selectedDate = t("Please select a date.");
      formValid = false;
    }
    if (!abnormalityArea) {
      updatedErrors.abnormalityArea = t("Please enter abnormality area.");
      formValid = false;
    }
    if (!typeOfAbnormality) {
      updatedErrors.typeOfAbnormality = t("Please enter type of abnormality.");
      formValid = false;
    }
    if (damagePercentage === "") {
      updatedErrors.damagePercentage = t("Please enter damage percentage.");
      formValid = false;
    }
    if (uploadedImages.length === 0) {
      updatedErrors.image = t("Please upload at least one image.");
      formValid = false;
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      ...updatedErrors,
    }));

    return formValid;
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const handleSubmit = async () => {
    const isFormValid = validateForm();
    if (isFormValid) {
      const formatDate = (date) => {
        const pad = (num) => (num < 10 ? `0${num}` : `${num}`);
        return date
          ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
              date.getDate()
            )}`
          : null;
      };
      const formData = new FormData();
      formData.append("date_of_activity", formatDate(selectedDate));
      formData.append("abnormality_area", abnormalityArea);
      formData.append("type_of_abnormality", typeOfAbnormality);
      formData.append("damage_percentage", parseInt(damagePercentage));
      uploadedImages.forEach((image) => {
        if (image instanceof File) {
          formData.append("images", image);
        }
      });

      if (isEditMode) {
        formData.append("id", existingData.id);
      } else {
        formData.append("farmer_plot", farmerPlotId);
      }
      const headers = {
        Authorization: `Token ${user.token}`,
        "Content-Type": "multipart/form-data",
      };

      const apiUrl = isEditMode
        ? "plotengagement/update-abnormalities"
        : "plotengagement/create-abnormalities";
      try {
        const response = await axios.post(
          `${SERVER_BASE_ADDRESS}/${apiUrl}`,
          formData,
          {
            headers,
          }
        );
        if (response.data) {
          setShowSuccessPopup(true);
          setTimeout(() => {
            setShowSuccessPopup(false);
            onClose();
          }, 2000);
        }
      } catch (error) {
        console.error("error creating/updating abnormalitites ", error);
        showAlert("error", "Some Error Occurred");
      }
    }
  };
  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        style={{ zIndex: 1 }}
        PaperProps={{ style: { maxHeight: "calc(100% - 25%)" } }}
      >
        <div style={{ padding: "16px" }}>
          <Grid container>
            <Grid item xs={10}>
              <DialogTitle style={{ padding: "0px" }}>
                {t("Abnormality")}
              </DialogTitle>
            </Grid>
            {existingData !== null && (
              <Grid item xs={1} style={{ marginTop: "10px" }}>
                <img
                  src={EditIcon}
                  onClick={() => setIsEditMode(true)}
                  alt="Edit"
                />
              </Grid>
            )}
            <Grid item xs={1}>
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={handleClose}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
            <Grid container style={{ marginTop: "30px" }}>
              {/* Date of Activity */}
              <Grid item xs={7}>
                <InputLabel className={classes.textLabel}>
                  {t("Date of Activity")}
                </InputLabel>
              </Grid>
              <Grid item xs={5}>
                <TextField
                  placeholder="DD/MM/YYYY"
                  disabled={!isEditMode && existingData !== null}
                  fullWidth
                  value={selectedDate ? selectedDate.toLocaleDateString() : ""}
                  helperText={errors.selectedDate}
                  error={!!errors.selectedDate}
                  InputProps={{
                    sx: {
                      height: "40px",
                      borderRadius: "5px",
                      marginTop: "10px",
                      background: "#FFF",
                      border: "1px solid #C7BDBD",
                      readOnly: true,
                    },
                    readOnly: true,
                    onClick: () =>
                      document.getElementById("date-activity-picker").click(),
                  }}
                />
                <DatePicker
                  id="date-activity-picker"
                  selected={selectedDate}
                  onChange={handleDateChange}
                  className={classes.datepicker}
                  dateFormat="dd/MM/yyyy"
                />
              </Grid>

              {/* Abnormality Area */}
              <Grid item xs={7}>
                <InputLabel className={classes.textLabel}>
                  {t("Abnormality Area")}
                </InputLabel>
              </Grid>
              <Grid item xs={5}>
                <TextField
                  fullWidth
                  value={abnormalityArea}
                  onChange={handleAbnormalityAreaChange}
                  helperText={errors.abnormalityArea}
                  error={!!errors.abnormalityArea}
                  InputProps={{
                    sx: {
                      height: "40px",
                      borderRadius: "5px",
                      marginTop: "10px",
                      background: "#FFF",
                      border: "1px solid #C7BDBD",
                    },
                  }}
                />
              </Grid>

              {/* Type of Abnormality */}
              <Grid item xs={7}>
                <InputLabel className={classes.textLabel}>
                  {t("Type of Abnormality")}
                </InputLabel>
              </Grid>
              <Grid item xs={5}>
                <FormControl fullWidth>
                  <Select
                    value={typeOfAbnormality}
                    onChange={handleTypeOfAbnormalityChange}
                    displayEmpty
                    error={!!errors.typeOfAbnormality}
                    inputProps={{ "aria-label": "Without label" }}
                    sx={{
                      height: "40px",
                      borderRadius: "5px",
                      marginTop: "10px",
                      background: "#FFF",
                      border: "1px solid #C7BDBD",
                    }}
                  >
                    <MenuItem value="">
                      <em>{t("Select Type")}</em>
                    </MenuItem>
                    <MenuItem value={"Low Germination"}>
                      {t("Low germination")}
                    </MenuItem>
                    <MenuItem value={"Seed rot"}>{t("Seed rot")}</MenuItem>
                    <MenuItem value={"Over irrigation"}>
                      {t("Over irrigation")}
                    </MenuItem>
                    <MenuItem value={"Seed dormant"}>
                      {t("Seed dormant")}
                    </MenuItem>
                    <MenuItem value={"Other"}>{t("Other")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Damage Percentage */}
              <Grid item xs={7}>
                <InputLabel className={classes.textLabel}>
                  {t("Damage Percentage")}
                </InputLabel>
              </Grid>
              <Grid item xs={5}>
                <TextField
                  fullWidth
                  value={damagePercentage}
                  onChange={handleDamageChange}
                  helperText={errors.damagePercentage}
                  error={!!errors.damagePercentage}
                  type="number"
                  InputProps={{
                    sx: {
                      height: "40px",
                      borderRadius: "5px",
                      marginTop: "10px",
                      background: "#FFF",
                      border: "1px solid #C7BDBD",
                    },
                  }}
                />
              </Grid>

              {/* Upload Image */}
              <Grid item xs={7}>
                <InputLabel className={classes.textLabel}>
                  {t("Please Upload Image")}
                </InputLabel>
              </Grid>
              <Grid item xs={5}>
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="upload-image"
                  type="file"
                  multiple
                  onChange={handleImageChange}
                />
                <label htmlFor="upload-image">
                  <CustomButton component="span" variant="contained" hideIcon>
                    {t("Upload")}
                  </CustomButton>
                </label>
                {errors.image && (
                  <Typography color="error">{errors.image}</Typography>
                )}
              </Grid>
            </Grid>
            <Box
              sx={{
                display: "flex",
                overflowX: "auto",
                marginTop: "20px",
                padding: 1,
              }}
            >
              {uploadedImages.map((image, index) => (
                <ImageCard
                  key={index}
                  imageUrl={
                    typeof image === "string"
                      ? image
                      : URL.createObjectURL(image)
                  } // Check type and set URL
                  onDelete={() => handleDeleteImage(image)} // Call delete handler
                />
              ))}
            </Box>
            <Grid item xs={12} style={{ display: "grid", padding: "10px" }}>
              <InputLabel>{t("Comment(Optional)")}</InputLabel>
              <TextField
                multiline
                rows={1}
                value={comment}
                onChange={handleCommentChange}
                disabled={!isEditMode && existingData !== null}
              />
            </Grid>
          </Grid>
        </div>
        <div
          style={{ display: "flex", margin: "auto", justifyContent: "center" }}
        >
          {existingData && isEditMode ? (
            <div style={{ width: "140px" }}>
              <CustomButton
                buttonText={t("Update")}
                handleOnChange={handleSubmit}
                hideIcon
              />
            </div>
          ) : !existingData && !isEditMode ? (
            <div
              style={{
                display: "flex",
                margin: "auto",
                justifyContent: "center",
                width: "140px",
              }}
            >
              <CustomButton
                buttonText={t("submit")}
                handleOnChange={handleSubmit}
                hideIcon
              />
            </div>
          ) : null}
        </div>
      </Dialog>
      {showSuccessPopup && (
        <SuccessPopup message={t("Data saved successfully!")} />
      )}
    </div>
  );
};

export default AbnormilityPopup;
